import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { ArrowRight, Heart, Users, Baby, Stethoscope, Brain, Activity } from 'lucide-react';

const audiences = [
  {
    icon: Users,
    title: 'Seniors & Elderly',
    description: 'Compassionate care for aging adults who wish to maintain independence and age gracefully in the comfort of their own home.',
    needs: ['Assistance with daily activities', 'Fall prevention and safety', 'Companionship and social engagement', 'Medication management support'],
    image: 'https://images.unsplash.com/photo-1758686253677-d3af6c15186e?crop=entropy&cs=srgb&fm=jpg&w=600&h=400&fit=crop',
  },
  {
    icon: Activity,
    title: 'Post-Surgery Recovery',
    description: 'Supportive care during recovery periods, helping individuals regain strength and independence after medical procedures.',
    needs: ['Mobility assistance', 'Wound care reminders', 'Medication schedule support', 'Light housekeeping during recovery'],
    image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=600&h=400&fit=crop',
  },
  {
    icon: Brain,
    title: 'Memory Care Support',
    description: 'Specialized support for individuals with Alzheimer\'s, dementia, or other cognitive conditions requiring patient, understanding care.',
    needs: ['Cognitive stimulation activities', 'Safety supervision', 'Routine establishment', 'Family respite support'],
    image: 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=600&h=400&fit=crop',
  },
  {
    icon: Heart,
    title: 'Chronic Condition Support',
    description: 'Ongoing assistance for those managing chronic health conditions, helping maintain quality of life and daily routines.',
    needs: ['Consistent daily support', 'Health monitoring', 'Appointment accompaniment', 'Lifestyle management'],
    image: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=600&h=400&fit=crop',
  },
  {
    icon: Stethoscope,
    title: 'Hospital Discharge Care',
    description: 'Transitional care to ensure a smooth, safe return home after hospitalization with proper support in place.',
    needs: ['Discharge planning support', 'Home safety preparation', 'Care coordination', 'Follow-up appointment management'],
    image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=600&h=400&fit=crop',
  },
  {
    icon: Baby,
    title: 'Family Caregivers',
    description: 'Respite and support services for families who need reliable help caring for their loved ones.',
    needs: ['Scheduled respite breaks', 'Emergency coverage', 'Caregiver education', 'Shared care responsibilities'],
    image: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=600&h=400&fit=crop',
  },
];

export const WhoWeServePage = () => {
  return (
    <>
      <Helmet>
        <title>Who We Serve | Heritage Home Health LLC</title>
        <meta
          name="description"
          content="Heritage Home Health LLC serves seniors, post-surgery patients, those with chronic conditions, and families needing respite care. Learn how we can help your loved ones."
        />
      </Helmet>
      <div className="min-h-screen bg-warm-beige" data-testid="who-we-serve-page">
        <Navigation />
        <main>
          {/* Hero Section */}
          <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-muted-green/10 via-transparent to-transparent" />
            <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-gradient-to-br from-soft-gold/20 to-transparent rounded-full blur-3xl" />
            
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                  Who We Serve
                </span>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy mb-6">
                  Caring for <span className="text-gradient-gold">Every Journey</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  Heritage Home Health LLC provides personalized care for individuals and families 
                  at every stage of life, adapting our services to meet unique needs and circumstances.
                </p>
              </motion.div>
            </div>
          </section>

          {/* Audiences Grid */}
          <section className="py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {audiences.map((audience, index) => {
                  const Icon = audience.icon;
                  return (
                    <motion.div
                      key={audience.title}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      className="group"
                      data-testid={`audience-card-${index}`}
                    >
                      <div className="h-full bg-white rounded-3xl shadow-soft-3d hover:shadow-soft-3d-lg transition-all duration-500 hover:-translate-y-2 overflow-hidden">
                        {/* Image */}
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={audience.image}
                            alt={audience.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                          <div className="absolute bottom-4 left-4 flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-xl bg-soft-gold/90 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-navy" />
                            </div>
                            <h3 className="font-serif text-xl text-white">{audience.title}</h3>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                          <p className="text-gray-600 mb-4">
                            {audience.description}
                          </p>
                          <h4 className="text-sm font-semibold text-navy mb-3">Common Care Needs:</h4>
                          <ul className="space-y-2 mb-6">
                            {audience.needs.map((need, i) => (
                              <li key={i} className="flex items-center space-x-2 text-sm text-gray-600">
                                <div className="w-1.5 h-1.5 rounded-full bg-soft-gold" />
                                <span>{need}</span>
                              </li>
                            ))}
                          </ul>
                          <Link
                            to="/contact"
                            className="inline-flex items-center text-sm font-semibold text-navy group-hover:text-soft-gold transition-colors"
                          >
                            <span>Learn More</span>
                            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Message */}
          <section className="py-16 md:py-20 bg-white">
            <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="font-serif text-3xl md:text-4xl text-navy mb-6">
                  Every Person Deserves <span className="text-gradient-gold">Dignified Care</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  At Heritage Home Health LLC, we believe that quality care should be accessible, 
                  personalized, and delivered with compassion. No matter your circumstances, 
                  we're here to help create a care plan that fits your unique needs.
                </p>
                <Link
                  to="/contact"
                  data-testid="who-we-serve-cta"
                  className="group inline-flex items-center space-x-2 bg-navy text-white px-8 py-4 rounded-full font-semibold hover:bg-deep-blue transition-all shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1"
                >
                  <span>Discuss Your Care Needs</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </section>

          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default WhoWeServePage;
